import { useState, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { HangarsTable } from '@/components/hangars/HangarsTable'
import { UnitsSheet } from '@/components/hangars/UnitsSheet'
import { DeleteHangarDialog } from '@/components/hangars/DeleteHangarDialog'
import { useHangars } from '@/hooks/useHangars'
import { useAppContext } from '@/hooks'
import { parseTypeFilter, AREA_GROUPS } from '@/types/areas'
import type { HangarTableRow } from '@/types/hangars'

/**
 * Get page title and description based on URL filter
 */
function getPageInfo(typeFilter: string | null): { title: string; description: string } {
  if (!typeFilter) {
    return {
      title: 'All Areas',
      description: 'Manage hangars, tiedown areas, and ramps across your locations',
    }
  }

  // Find matching group by checking if types match
  const filterTypes = parseTypeFilter(typeFilter)
  const matchingGroup = AREA_GROUPS.find((group) => {
    const groupTypes = [...group.types].sort()
    const filterSorted = [...filterTypes].sort()
    return (
      groupTypes.length === filterSorted.length &&
      groupTypes.every((t, i) => t === filterSorted[i])
    )
  })

  if (matchingGroup) {
    return {
      title: matchingGroup.label,
      description: matchingGroup.description,
    }
  }

  // Fallback for custom filter
  return {
    title: 'Filtered Areas',
    description: `Showing ${filterTypes.length} area types`,
  }
}

/**
 * Areas management page
 * Displays list of buildings/areas with CRUD operations
 * Supports URL-based type filtering via ?type=... parameter
 */
export function AreasPage() {
  // Get location from URL context
  const { locationId, locationSlug, scope } = useAppContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const typeFilter = searchParams.get('type')

  // Parse filter from URL
  const allowedTypes = useMemo(() => parseTypeFilter(typeFilter), [typeFilter])
  const { title, description } = useMemo(() => getPageInfo(typeFilter), [typeFilter])

  // Dialog state
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  // Units configuration sheet state
  const [unitsSheetState, setUnitsSheetState] = useState<{
    open: boolean
    hangarId?: string
  }>({ open: false })

  // Fetch hangars, filtered by location when in location scope
  const hangarFilters = scope === 'location' && locationId ? { locationId } : undefined
  const { data: hangars, isLoading } = useHangars(hangarFilters)

  // Filter by type if filter is set
  const filteredHangars = useMemo(() => {
    if (!hangars) return []
    if (allowedTypes.length === 0) return hangars

    return hangars.filter((h: HangarTableRow) =>
      allowedTypes.includes(h.type)
    )
  }, [hangars, allowedTypes])

  const handleDelete = (id: string, name: string) => {
    setDeleteTarget({ id, name })
  }

  const handleDuplicate = (id: string) => {
    if (locationSlug) {
      navigate(`/${locationSlug}/builder?duplicate=${id}`)
    }
  }

  const handleConfigureUnits = (hangarId: string) => {
    setUnitsSheetState({ open: true, hangarId })
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground">{description}</p>
        </div>
      </div>

      {/* Areas Table */}
      <HangarsTable
        data={filteredHangars}
        isLoading={isLoading}
        onDuplicate={locationSlug ? handleDuplicate : undefined}
        onDelete={handleDelete}
        onConfigureUnits={handleConfigureUnits}
      />

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteHangarDialog
          open={!!deleteTarget}
          onOpenChange={handleCloseDeleteDialog}
          hangarId={deleteTarget.id}
          hangarName={deleteTarget.name}
        />
      )}

      {/* Units Configuration Sheet */}
      {unitsSheetState.hangarId && (
        <UnitsSheet
          hangarId={unitsSheetState.hangarId}
          open={unitsSheetState.open}
          onOpenChange={(open) =>
            setUnitsSheetState((prev) => ({ ...prev, open }))
          }
        />
      )}
    </div>
  )
}
